import { Logger, LogManager, StringUtil } from "@mcleod/core";
import { AdvancedDecorator } from "./AdvancedDecorator";
import { CrudDecorator } from "./CrudDecorator";
import { DataSourceMode } from "../../databinding/DataSource";
import { Decorator } from "./Decorator";
import { DecoratorAccessTester, DecoratorAccessTestResult } from "./DecoratorAccessTester";
import { DecoratorType } from "./DecoratorType";
import { Layout } from "../../components/layout/Layout";
import { Panel } from "../..";
import { PanelStaticError } from "../PanelStaticError";
import { ReportDecorator } from "./ReportDecorator";
import { TaskDecorator } from "./TaskDecorator";

export class DecoratorFactory {
    private static log: Logger;

    /**
     * This determines which decorator should be used for a page given the page's properties and the current browser URL
     * @param {Layout} layout
     * @returns
     */
    public static decoratePage(layout: Layout, searchParams?: string | URLSearchParams): Panel {
        const finalizedSearchParams = this.finalizeSearchParams(searchParams);
        const props = this.createProps(layout, finalizedSearchParams);
        const modeFromUrl = finalizedSearchParams?.get("mode");
        const proposedDecorator = !StringUtil.isEmptyString(modeFromUrl) ? modeFromUrl : layout.defaultUse;
        this.getLog().debug("Proposed decorator: %o", proposedDecorator);
        let decoratorType = this.resolveDecoratorType(proposedDecorator);
        const decoratorAccessTestResult = DecoratorAccessTester.isDecoratorAllowed(layout, decoratorType);

        // Part of the temporary change in DecoratorAccessTester.testAllowedUses()
        // When decoratorAccessTest.allowed = undefined, don't decorator the page but also don't show the broken pallet
        // error page
        if (decoratorAccessTestResult.allowed === undefined) {
            decoratorType = null;
        }
        if (decoratorAccessTestResult.allowed !== false) {
            return this.createDecorator(decoratorType, layout, props, decoratorAccessTestResult);
        } else {
            return new PanelStaticError(decoratorAccessTestResult.message);
        }

        // Permananent code to use later
        // if (decoratorAccessTestResult.allowed === true) {
        //     return this.createDecorator(decoratorType, layout, props, decoratorAccessTestResult);
        // } else {
        //     return new PanelStaticError(decoratorAccessTestResult.message);
        // }
    }

    private static finalizeSearchParams(searchParams: string|URLSearchParams): URLSearchParams {
        // Check for undefined - allow passing null to indicate "I don't want any searchParams"
        if (searchParams === undefined) {
            searchParams = window.location.search;
        }
        if (typeof searchParams === "string") {
            searchParams = new URLSearchParams(searchParams);
        }
        this.getLog().debug("Finalized search params: %o", searchParams);
        return searchParams;
    }

    private static createProps(layout: Layout, searchParams: URLSearchParams) {
        const result = { layout: layout };
        if (searchParams != null) {
            searchParams.forEach((value: string, key: string, _parent: URLSearchParams) => result[key] = value);
        }
        this.getLog().debug("Created decorator props: %o", result);
        return result;
    }

    /**
     * Converts a string, DataSourceMode, or existing DecoratorType to a DecoratorType value
     * @param value - The value to convert (string, DataSourceMode, or DecoratorType)
     * @returns The corresponding DecoratorType or null if no matching type found
     */
    public static resolveDecoratorType(value: string|DecoratorType|DataSourceMode): DecoratorType {
        const normalizedValue = typeof value === "string" ? value.toLowerCase() : value;
        if (Object.values(DecoratorType).includes(normalizedValue as DecoratorType)) {
            this.getLog().debug("Resolved %o to decorator type %o", value, normalizedValue);
            return normalizedValue as DecoratorType;
        }
        return null;
    }

    private static createDecorator(decoratorType: DecoratorType, layout: Layout, props: any,
        decoratorAccessTestResult: DecoratorAccessTestResult): Layout|Decorator {
        if (decoratorType == null) {
            return layout;
        }
        let result: Decorator;
        switch (decoratorType) {
            case DecoratorType.ADVANCED:
                result = new AdvancedDecorator(props);
                break;
            case DecoratorType.SEARCH:
                result = new CrudDecorator({ mode: DataSourceMode.SEARCH, ...props });
                break;
            case DecoratorType.ADD:
                result = new CrudDecorator({ mode: DataSourceMode.ADD, ...props });
                break;
            case DecoratorType.UPDATE:
                result = new CrudDecorator({ mode: DataSourceMode.UPDATE, ...props });
                break;
            case DecoratorType.REPORT:
                result = new ReportDecorator({ ...props });
                break;
            case DecoratorType.TASK:
                result = new TaskDecorator({ ...props });
                break;
            default:
                throw new Error("Invalid page decorator " + decoratorType);
        }
        result.accessTestResult = decoratorAccessTestResult;
        return result;
    }

    private static getLog(): Logger {
        if (this.log == null)
            this.log = LogManager.getLogger("components.page.decorators.DecoratorFactory");
        return this.log;
    }
}
